import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { WidthProps } from 'styled-system';
import { backgroundColor, borderRadius, gridGap, position, space, width } from 'styled-system';

import { Flex } from 'common/components/Flex';
import RoundedRectShape from 'common/components/RoundedRectShape';
import { boxSizing, themeTop } from 'common/theme/system-utilities';

interface PlaceholderProps {
  itemsCount: number;
  itemWidth: WidthProps['width'];
}

const TEXT_PLACEHOLDER_HEIGHT = 14;

const UnstyledContainer = styled(Flex)<any>`
  ${gridGap};
`;

const Container = (props: ComponentProps<typeof UnstyledContainer>) => (
  <UnstyledContainer
    width={1}
    mb={['sp_16', 'sp_20', 'sp_24']}
    flexWrap="wrap"
    gridGap={['sp_4', 'sp_8', 'sp_12', 'sp_16']}
    {...props}
  />
);

const UnstyledProductCard = styled(Flex)<any>`
  ${backgroundColor};
  ${borderRadius};
  ${boxSizing};
`;

const ProductCard = (props: ComponentProps<typeof UnstyledProductCard>) => (
  <UnstyledProductCard
    flexDirection="column"
    bg="sys.neutral.background.default"
    p="sp_8"
    pb={['sp_16', null, null, 'sp_20']}
    borderRadius="medium"
    boxSizing="border-box"
    {...props}
  />
);

const UnstyledImageContainer = styled.div<any>`
  ${position};
  ${space};
  ${width};
`;

const ImageContainer = (props: ComponentProps<typeof UnstyledImageContainer>) => (
  <UnstyledImageContainer position="relative" pt="100%" width={1} mb="sp_12" {...props} />
);

const UnstyledImage = styled(RoundedRectShape)<any>`
  ${position};
  ${themeTop};
`;

const Image = (props: ComponentProps<typeof UnstyledImage>) => (
  <UnstyledImage
    height="100%"
    borderRadius="medium"
    position="absolute"
    themeTop="sp_0"
    {...props}
  />
);

const UnstyledTextPlaceholder = styled(RoundedRectShape)``;

const TextPlaceholder = (props: ComponentProps<typeof UnstyledTextPlaceholder>) => (
  <UnstyledTextPlaceholder height={TEXT_PLACEHOLDER_HEIGHT} mt="sp_8" {...props} />
);

const Placeholder = ({ itemWidth, itemsCount }: PlaceholderProps) => (
  <Container className="show-loading-animation" data-testid="product-list-placeholder">
    {[...Array(itemsCount)].map((_, index) => (
      <ProductCard width={itemWidth} key={index}>
        <ImageContainer>
          <Image />
        </ImageContainer>
        <TextPlaceholder key="product-name" />
        <TextPlaceholder key="brand" />
        <TextPlaceholder key="price" width={86} height={20} mt="sp_16" mb="sp_8" />
        <TextPlaceholder key="delivery" width={9 / 10} />
        <TextPlaceholder key="ratings" width={4 / 5} />
      </ProductCard>
    ))}
  </Container>
);

export default Placeholder;
